// Envio da OS ao Omie direto do formulário (services/realizado_faturamento_os_omie.py). Modo
// REAL: a plataforma cria a OS (AlterarContrato + OS na 1ª solicitação do contrato no mês, depois
// só OS) — o agendador fica pausado. Modo SIMULACAO: só monta os payloads e confere com a OS
// existente no Omie, sem gravar nada.
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FlaskConical, RefreshCw } from "lucide-react";
import {
  conferirRealizadoOmieOs, enviarRealizadoOmieOs, getRealizadoOmieOs, getRealizadoOmieOsStatus, simularRealizadoOmieOs, simularRealizadoOmieOsLote,
} from "../../api";
import { AvisoErro, Badge, Card, Modal, botaoPequeno, formatData, formatValor, inputStyle } from "./comum";

const ACOES = { ALTERAR_CONTRATO_E_OS: "AlterarContrato + OS", SOMENTE_OS: "Só OS" };

const ALERTAS = { erro: "Erro no envio", sem_os: "Sem OS no Omie", sem_nf: "OS sem nota", os_faturada: "OS faturada (conferida)" };

export function situacaoOmie(s, C) {
  const alertas = s.omieAlertas || {};
  if (s.omieStatus === "ENVIADO") {
    if (alertas.sem_nf) return { rotulo: "OS sem nota", cor: C.warning, dica: "OS criada, ainda não faturada — alerta enviado" };
    return { rotulo: `OS ${s.omieOsNumero || "criada"}`, cor: C.success, dica: "OS criada no Omie" };
  }
  if (s.omieStatus === "PENDENTE") return { rotulo: "Tentando de novo", cor: C.warning, dica: s.omieErro };
  if (s.omieStatus === "ERRO_DADOS") return { rotulo: "Erro: corrigir dados", cor: C.danger, dica: s.omieErro };
  if (s.omieStatus === "ERRO_TECNICO") return { rotulo: "Erro técnico", cor: C.danger, dica: s.omieErro };
  if (alertas.sem_os) return { rotulo: "Sem OS (alertado)", cor: C.danger, dica: "A conferência não encontrou OS no Omie — alerta enviado por e-mail" };
  if (alertas.sem_nf) return { rotulo: "OS sem nota (alertado)", cor: C.warning, dica: "OS não faturada — alerta enviado por e-mail" };
  if (s.omieModo === "REAL" && s.origem === "PLATAFORMA" && !["ENVIADO", "PENDENTE", "ERRO_DADOS", "ERRO_TECNICO"].includes(s.omieStatus)) {
    // Feita antes da virada (só simulada) ou ainda na fila do envio em segundo plano.
    return s.omieConferencia === "OS_ENCONTRADA"
      ? { rotulo: "OS do agendador", cor: C.success, dica: "Feita antes da virada: o agendador já criou a OS no Omie" }
      : { rotulo: "Não enviada", cor: C.warning, dica: "Sem OS da plataforma — abra para enviar ao Omie" };
  }
  if (!s.omieStatus) return { rotulo: "—", cor: C.muted, dica: "Ainda não simulada" };
  if (s.omieStatus === "ERRO_SIMULACAO") return { rotulo: "Erro", cor: C.danger, dica: s.omieErro };
  if (s.omieConferencia === "OS_ENCONTRADA") {
    return s.omieDiferencas
      ? { rotulo: `OS com ${s.omieDiferencas} diferença(s)`, cor: C.warning, dica: "A OS do Omie difere da simulada" }
      : { rotulo: "OS conferida", cor: C.success, dica: "A OS do Omie bate com a simulada" };
  }
  return { rotulo: "OS não encontrada", cor: C.muted, dica: "Nenhuma OS com o mesmo cliente, valor e data de previsão" };
}

function Linha({ C, rotulo, children }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
      <span style={{ color: C.muted, minWidth: 150 }}>{rotulo}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );
}

function JsonExpansivel({ C, titulo, dados }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setAberto((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: C.brand, cursor: "pointer", fontSize: 12, fontWeight: 700, padding: 0, fontFamily: "inherit" }}>
        {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {titulo}
      </button>
      {aberto && (
        <pre style={{ margin: "6px 0 0", fontSize: 11, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", color: C.text }}>
          {JSON.stringify(dados, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ResumoOs({ C, os }) {
  return (
    <span>
      OS <b>{os.cNumOS}</b> · origem {os.origem === "API" ? "API (IncluirOS)" : os.origem === "CTR" ? "contrato (CTR)" : os.origem || "?"} · etapa {os.cEtapa}
      {os.faturada ? " · faturada" : ""}{os.cancelada ? " · CANCELADA" : ""} · previsão {os.dDtPrevisao} · incluída {os.incluidaEm}
    </span>
  );
}

export function ModalOmieOs({ C, solicitacaoId, onClose, onAtualizado }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [simulando, setSimulando] = useState(false);

  useEffect(() => {
    getRealizadoOmieOs(solicitacaoId).then(setDados).catch((e) => setErro(e.message));
  }, [solicitacaoId]);

  const executar = async (acao) => {
    setSimulando(true);
    setErro("");
    try {
      setDados(await acao(solicitacaoId));
      onAtualizado?.();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSimulando(false);
    }
  };

  const enviarPrimeiraVez = async () => {
    const osExistente = conf.situacao === "OS_ENCONTRADA";
    const aviso = osExistente
      ? `ATENÇÃO: já existe no Omie a OS ${conf.os?.cNumOS} com o mesmo cliente, valor e data (provavelmente criada pelo agendador). Enviar vai criar OUTRA OS. Enviar mesmo assim?`
      : "Criar a OS desta solicitação no Omie agora?";
    if (window.confirm(aviso)) await executar(enviarRealizadoOmieOs);
  };

  const sim = dados?.simulacao || {};
  const conf = sim.conferencia || {};
  const real = dados?.modo === "REAL";
  const statusReal = ["ENVIADO", "PENDENTE", "ERRO_DADOS", "ERRO_TECNICO"].includes(dados?.status);
  const todas = conf.os ? [conf.os, ...(conf.outras || [])] : [];
  const duplicadaApiCtr = todas.some((o) => o.origem === "API") && todas.some((o) => o.origem === "CTR" && !o.cancelada);

  return (
    <Modal
      C={C}
      largura={820}
      titulo={`${real ? "Envio ao Omie" : "Simulação do envio ao Omie"} · solicitação ${solicitacaoId}`}
      onClose={onClose}
      rodape={
        <>
          {["ERRO_DADOS", "ERRO_TECNICO", "PENDENTE"].includes(dados?.status) && (
            <button style={botaoPequeno(C.brand)} disabled={simulando} onClick={() => executar(enviarRealizadoOmieOs)}>
              <RefreshCw size={13} /> {simulando ? "Enviando…" : "Reenviar ao Omie"}
            </button>
          )}
          {real && dados?.origem === "PLATAFORMA" && dados && !statusReal && (
            <button style={botaoPequeno(C.brand)} disabled={simulando} onClick={enviarPrimeiraVez}>
              <RefreshCw size={13} /> {simulando ? "Enviando…" : "Enviar ao Omie"}
            </button>
          )}
          {!statusReal && (
            <button style={botaoPequeno(C.brand, true)} disabled={simulando} onClick={() => executar(simularRealizadoOmieOs)}>
              <FlaskConical size={13} /> {simulando ? "Simulando…" : "Simular de novo"}
            </button>
          )}
        </>
      }
    >
      <AvisoErro C={C}>{erro}</AvisoErro>
      {!dados ? <div style={{ fontSize: 12, color: C.muted }}>Carregando…</div> : !dados.status ? (
        <div style={{ fontSize: 12, color: C.muted }}>Ainda não simulada. Clique em “Simular de novo”.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            {sim.modo === "REAL" ? "Envio real ao Omie" : "Simulação: nada foi gravado no Omie"}. Processado em {formatData(dados.processadoEm)} {dados.processadoEm?.slice(11, 16)}.
          </div>
          {dados.osNumero && <Linha C={C} rotulo="OS criada">nº {dados.osNumero} (código {dados.osCodigo})</Linha>}
          {dados.status === "PENDENTE" && (
            <Linha C={C} rotulo="Nova tentativa">{dados.tentativas}ª falha técnica · próxima tentativa automática às {dados.proximaTentativa?.slice(11, 16)} (UTC)</Linha>
          )}
          {Object.keys(dados.alertas || {}).length > 0 && (
            <Linha C={C} rotulo="Alertas por e-mail">
              {Object.entries(dados.alertas).map(([k, v]) => `${ALERTAS[k] || k} (${formatData(v)})`).join(" · ")}
            </Linha>
          )}
          {sim.erro && (
            <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 8 }}>
              <b>{sim.erro.codigo}</b>: {sim.erro.mensagem}
            </div>
          )}
          <Linha C={C} rotulo="Empresa · valor">{sim.dados?.empresa} · R$ {formatValor(sim.dados?.valor)} · faturamento {formatData(sim.dados?.data_faturamento)}</Linha>
          <Linha C={C} rotulo="Contrato">{sim.dados?.numero_contrato}{sim.contrato ? ` → nCodCtr ${sim.contrato.nCodCtr}` : ""}</Linha>
          {sim.cliente && <Linha C={C} rotulo="Cliente no Omie">{sim.cliente.razao_social} ({sim.cliente.cnpj}) · {sim.cliente.codigo}</Linha>}
          {sim.vendedor && <Linha C={C} rotulo="Vendedor (GP)">{sim.vendedor.nome} · {sim.vendedor.codigo}</Linha>}
          {dados.acao && (
            <Linha C={C} rotulo="Ação">
              <b>{ACOES[dados.acao]}</b> · {sim.ordemNoMes}ª solicitação do contrato com faturamento neste mês
            </Linha>
          )}
          {"regraEmissao" in sim && (
            <Linha C={C} rotulo="Regras 2">
              {sim.regraEmissao ? `prazo ${sim.regraEmissao.prazo} dia(s)${sim.regraEmissao.observacao ? ` · obs.: ${sim.regraEmissao.observacao}` : ""}` : "sem regra para este contrato (prazo 0, sem observação)"}
            </Linha>
          )}

          {conf.situacao && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Conferência com o Omie</div>
              {conf.situacao === "OS_ENCONTRADA" ? (
                <>
                  {todas.map((o) => <Linha key={o.nCodOS} C={C} rotulo={o === conf.os ? "OS comparada" : "Outra OS na data"}><ResumoOs C={C} os={o} /></Linha>)}
                  {conf.diferencas.length === 0 ? (
                    <div style={{ fontSize: 12, color: C.success, marginTop: 6 }}>✓ A OS simulada bate com a do Omie (data, vendedor, centro de custo, projeto e vencimento).</div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      {conf.diferencas.map((d) => (
                        <div key={d.campo} style={{ fontSize: 12, color: C.warning }}>⚠ {d.campo}: simulado <b>{String(d.simulado)}</b> × Omie <b>{String(d.omie)}</b></div>
                      ))}
                    </div>
                  )}
                  {duplicadaApiCtr && (
                    <div style={{ fontSize: 12, color: C.warning, marginTop: 6 }}>
                      ⚠ Há uma OS da API (IncluirOS do agendador) e outra gerada pelo contrato (CTR) na mesma data — a da API costuma ficar parada na etapa 20.
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: C.muted }}>Nenhuma OS no Omie com o mesmo cliente, valor e data de previsão.</div>
              )}
              {(conf.outrasDatas || []).length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: C.muted }}>OS de mesmo cliente e valor em outras datas:</div>
                  {conf.outrasDatas.map((o) => <div key={o.nCodOS} style={{ fontSize: 11, color: C.muted }}>• <ResumoOs C={C} os={o} /></div>)}
                </div>
              )}
            </div>
          )}

          {(sim.etapas || []).map((e) => <JsonExpansivel key={e.call} C={C} titulo={`Payload ${e.call} (sem credenciais)`} dados={e.param} />)}
        </>
      )}
    </Modal>
  );
}

export function PainelSimulacaoOmie({ C, onConcluido }) {
  const [execucao, setExecucao] = useState(null);
  const [desde, setDesde] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [erro, setErro] = useState("");

  const consultar = useCallback(() => getRealizadoOmieOsStatus().then(setExecucao).catch((e) => setErro(e.message)), []);
  useEffect(() => { consultar(); }, [consultar]);
  useEffect(() => {
    if (execucao?.status !== "EXECUTANDO") return undefined;
    const t = setInterval(() => {
      getRealizadoOmieOsStatus().then((r) => {
        setExecucao(r);
        if (r?.status !== "EXECUTANDO") onConcluido?.();
      });
    }, 4000);
    return () => clearInterval(t);
  }, [execucao?.status, onConcluido]);

  const iniciar = async () => {
    setErro("");
    try { setExecucao(await simularRealizadoOmieOsLote(desde)); } catch (e) { setErro(e.message); }
  };
  const [conferindo, setConferindo] = useState(false);
  const conferirAgora = async () => {
    setConferindo(true);
    setErro("");
    try { await conferirRealizadoOmieOs(); await consultar(); onConcluido?.(); } catch (e) { setErro(e.message); } finally { setConferindo(false); }
  };
  const executando = execucao?.status === "EXECUTANDO";
  const r = execucao?.resumo;
  const conf = execucao?.conferencia;

  return (
    <Card
      C={C}
      titulo={<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FlaskConical size={15} /> Envio da OS ao Omie · {execucao?.modo === "REAL" ? "envio real" : "simulação"}</span>}
      subtitulo={execucao?.modo === "REAL"
        ? "Cada envio do formulário cria a OS no Omie em segundo plano. Falha técnica tenta de novo sozinha; erro de dados avisa por e-mail na hora."
        : "Cada envio do formulário já é simulado automaticamente (nada é gravado no Omie). Aqui dá para simular as solicitações desde uma data e conferir com as OS que o agendador criou."}
      acoes={
        <>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} disabled={executando} style={{ ...inputStyle(C), minHeight: 34, padding: "5px 8px", fontSize: 12, width: "auto" }} />
          <button style={{ ...botaoPequeno(C.brand), opacity: executando ? 0.6 : 1 }} disabled={executando || !desde} onClick={iniciar}>
            <RefreshCw size={13} /> {executando ? "Simulando…" : "Simular desde a data"}
          </button>
          <button style={botaoPequeno(C.brand, true)} disabled={conferindo} onClick={conferirAgora} title="Confere agora as solicitações sem OS / OS sem nota e envia o alerta por e-mail, se houver">
            {conferindo ? "Conferindo…" : "Conferir agora"}
          </button>
        </>
      }
    >
      <AvisoErro C={C}>{erro}</AvisoErro>
      {execucao?.id ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, alignItems: "center" }}>
          <Badge cor={execucao.status === "OK" ? C.success : execucao.status === "ERRO" ? C.danger : C.warning}>
            {{ OK: "Concluída", ERRO: "Erro", EXECUTANDO: "Em andamento" }[execucao.status]}
          </Badge>
          <span style={{ color: C.muted }}>desde {formatData(execucao.parametros?.desde)} · {execucao.usuario}</span>
          {r && (
            <>
              <span><b>{r.total}</b> simuladas</span>
              <span style={{ color: C.success }}><b>{r.osEncontrada - r.comDiferencas}</b> OS conferidas</span>
              <span style={{ color: C.warning }}><b>{r.comDiferencas}</b> com diferença</span>
              <span><b>{r.osNaoEncontrada}</b> sem OS no Omie</span>
              <span style={{ color: r.ERRO_SIMULACAO ? C.danger : C.muted }}><b>{r.ERRO_SIMULACAO}</b> erro(s)</span>
            </>
          )}
          {execucao.erro && <span style={{ color: C.danger }}>{execucao.erro}</span>}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.muted }}>Nenhuma simulação em lote ainda.</div>
      )}
      <div style={{ fontSize: 12, color: C.muted, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
        <b>Conferência automática (a cada hora):</b> solicitação sem OS no Omie depois de 2h e OS sem nota depois de 3 dias úteis geram alerta por e-mail ao financeiro.{" "}
        {conf ? (
          <>
            Última: {formatData(conf.iniciadoEm)} {conf.iniciadoEm.slice(11, 16)} (UTC) ·{" "}
            {conf.status === "ERRO" ? <span style={{ color: C.danger }}>erro: {conf.erro}</span> : (
              <>{conf.resumo?.conferidas ?? 0} conferida(s) · <span style={{ color: conf.resumo?.semOs?.length ? C.danger : C.muted }}>{conf.resumo?.semOs?.length ?? 0} sem OS</span> · <span style={{ color: conf.resumo?.semNota?.length ? C.warning : C.muted }}>{conf.resumo?.semNota?.length ?? 0} OS sem nota</span>{conf.resumo?.emailEnviado ? " · e-mail enviado" : ""}</>
            )}
          </>
        ) : "Ainda não rodou."}
      </div>
    </Card>
  );
}
